import { countries } from "countries-list";

/** Set of valid ISO 2-letter country codes (for when API sends code instead of name) */
const VALID_COUNTRY_CODES = new Set(Object.keys(countries));

/** Map country name -> ISO 3166-1 alpha-2 code (e.g. "United Arab Emirates" -> "AE") */
const countryNameToCode: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const [code, data] of Object.entries(countries)) {
    map[data.name] = code;
    map[data.name.toLowerCase()] = code;
    map[data.name.toUpperCase()] = code;
  }
  return map;
})();

/** Common aliases / alternate names that API might send instead of full country name */
const COUNTRY_ALIASES: Record<string, string> = {
  uk: "GB",
  uae: "AE",
  usa: "US",
  "u.s.a.": "US",
  "u.s.a": "US",
  "united states": "US",
  "united states of america": "US",
  "united kingdom": "GB",
  "great britain": "GB",
  england: "GB",
  scotland: "GB",
  wales: "GB",
  "northern ireland": "GB",
  korea: "KR",
  "south korea": "KR",
  "north korea": "KP",
  tanzania: "TZ",
  "united republic of tanzania": "TZ",
  vietnam: "VN",
  "viet nam": "VN",
  russia: "RU",
  "russian federation": "RU",
  iran: "IR",
  "iran (islamic republic of)": "IR",
  syria: "SY",
  "syrian arab republic": "SY",
  palestine: "PS",
  "state of palestine": "PS",
  bolivia: "BO",
  "bolivia (plurinational state of)": "BO",
  venezuela: "VE",
  "venezuela (bolivarian republic of)": "VE",
  taiwan: "TW",
  "taiwan, province of china": "TW",
  moldova: "MD",
  "republic of moldova": "MD",
  pakistan: "PK",
  "pakistan (islamic republic of)": "PK",
  canada: "CA",
};

const FLAG_CDN_BASE = "https://flagcdn.com";
const FLAG_SIZE = "24x18";

/** English cities: only used when country is already UK/GB to show England flag (gb-eng). */
const ENGLAND_CITIES = new Set(
  [
    "London",
    "Birmingham",
    "Manchester",
    "Leeds",
    "Liverpool",
    "Bristol",
    "Sheffield",
    "Newcastle",
    "Newcastle upon Tyne",
    "Nottingham",
    "Leicester",
    "Southampton",
    "Brighton",
    "Plymouth",
    "Wolverhampton",
    "Reading",
    "Derby",
    "Stoke-on-Trent",
    "York",
    "Northampton",
    "Luton",
    "Oxford",
    "Cambridge",
    "Portsmouth",
    "Milton Keynes",
    "Ipswich",
    "Norwich",
    "Exeter",
    "Cheltenham",
    "Gloucester",
    "Bath",
    "Bournemouth",
    "Swindon",
    "Peterborough",
    "Lincoln",
    "Canterbury",
    "Colchester",
    "Southend",
    "Maidstone",
    "Hull",
    "Kingston upon Hull",
  ].map((c) => c.toLowerCase().trim())
);

/**
 * Cities that strongly imply Pakistan when country is missing.
 * Avoid ambiguous names (e.g. Hyderabad also exists in India).
 */
const PAKISTAN_ONLY_CITIES = new Set(
  [
    "Haripur",
    "Islamabad",
    "Karachi",
    "Lahore",
    "Faisalabad",
    "Rawalpindi",
    "Multan",
    "Peshawar",
    "Quetta",
    "Sialkot",
    "Gujranwala",
    "Sargodha",
    "Bahawalpur",
    "Sukkur",
    "Abbottabad",
    "Mardan",
    "Mingora",
    "Dera Ghazi Khan",
    "Sheikhupura",
    "Rahim Yar Khan",
    "Jhang",
    "Gujrat",
    "Sahiwal",
    "Wah Cantonment",
    "Kasur",
    "Okara",
    "Dera Ismail Khan",
    "Chiniot",
  ].map((c) => c.toLowerCase().trim())
);

function normalizeCityName(city: string | undefined | null): string {
  return (city ?? "").trim().toLowerCase();
}

function isCityInEngland(city: string | undefined | null): boolean {
  const c = normalizeCityName(city);
  return c.length > 0 && ENGLAND_CITIES.has(c);
}

function isPakistanOnlyCity(city: string | undefined | null): boolean {
  const c = normalizeCityName(city);
  return c.length > 0 && PAKISTAN_ONLY_CITIES.has(c);
}

/**
 * Returns ISO 2-letter country code from country name (or code), or null if not found.
 */
export function getCountryCode(
  countryName: string | undefined | null
): string | null {
  const name = (countryName ?? "").trim();
  if (!name) return null;
  const twoLetter = name.length === 2 ? name.toUpperCase() : null;
  if (twoLetter && VALID_COUNTRY_CODES.has(twoLetter)) return twoLetter;
  const code =
    countryNameToCode[name] ??
    countryNameToCode[name.toLowerCase()] ??
    COUNTRY_ALIASES[name.toLowerCase()];
  return code ?? null;
}

/**
 * Localized country name for UI (filter labels, badges).
 */
export function formatCountryForLocale(
  countryName: string | undefined | null,
  locale: string
): string {
  const name = (countryName ?? "").trim();
  if (!name) return "";
  const code = getCountryCode(name);
  if (!code) return name;
  try {
    const locales = locale && locale.length > 0 ? [locale] : ["en"];
    const dn = new Intl.DisplayNames(locales, { type: "region" });
    return dn.of(code) ?? name;
  } catch {
    return name;
  }
}

function flagUrlForCode(code: string): string {
  return `${FLAG_CDN_BASE}/${FLAG_SIZE}/${code.toLowerCase()}.png`;
}

/**
 * If city looks like "London, Canada" or "Haripur, Pakistan", return { city, country }.
 */
function parseCityCountryCombo(
  city: string | undefined | null
): { city: string; country: string } | null {
  const raw = (city ?? "").trim();
  if (!raw || !raw.includes(",")) return null;
  const parts = raw
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2) return null;
  const maybeCountry = parts[parts.length - 1];
  const maybeCity = parts.slice(0, -1).join(", ");
  if (!getCountryCode(maybeCountry)) return null;
  return { city: maybeCity, country: maybeCountry };
}

function flagForResolvedCountry(
  countryCode: string,
  cityName: string | undefined | null
): string {
  if (countryCode === "GB" && isCityInEngland(cityName)) {
    return flagUrlForCode("gb-eng");
  }
  return flagUrlForCode(countryCode);
}

/**
 * Returns flag image URL from flagcdn.com for the given country (+ optional city).
 *
 * Priority:
 * 1. Country parsed from city ("London, Canada") — fixes stale country field
 * 2. Explicit country field
 * 3. Unambiguous city-only inference (never overrides a different country)
 */
export function getCountryFlagUrl(
  countryName: string | undefined | null,
  city?: string | undefined | null
): string | null {
  const countryTrimmed = (countryName ?? "").trim();
  const cityTrimmed = (city ?? "").trim();
  const combo = parseCityCountryCombo(cityTrimmed);
  const cityForMatch = combo?.city || cityTrimmed;

  // 1) "London, Canada" in city wins over a stale country field (e.g. still Pakistan).
  if (combo) {
    const comboCode = getCountryCode(combo.country);
    if (comboCode) {
      return flagForResolvedCountry(comboCode, combo.city);
    }
  }

  // 2) Explicit country field.
  const countryCode = getCountryCode(countryTrimmed);
  if (countryCode) {
    return flagForResolvedCountry(countryCode, cityForMatch);
  }

  // 3) Country missing: only infer from unambiguous Pakistan cities.
  // Do NOT infer England from city alone (London also exists in Canada).
  if (cityForMatch && isPakistanOnlyCity(cityForMatch)) {
    return flagUrlForCode("PK");
  }

  return null;
}
